/**
 * Tema da landing page (rota "/") — mesma pegada do site do Control Zone.
 */
import styled from "styled-components";

export const MarketingPage = styled.div`
  /* dentro do DefaultLayout (flex): ocupa a largura toda */
  width: 100%;
  flex: 1 1 auto;
  align-self: flex-start;
  min-height: 100vh;
  background: #f4f6fb;
  color: #141829;
  position: relative;
  overflow-x: hidden;
  scroll-behavior: smooth;

  a:hover {
    text-decoration: none;
  }
`;

export const HeaderShell = styled.header`
  position: sticky;
  top: 0;
  z-index: 100;
  padding: 14px 16px;
  background: #0c1028;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);

  box-shadow: ${({ $scrolled }) =>
    $scrolled ? "0 8px 32px rgba(0, 0, 0, 0.35)" : "none"};
  transition: box-shadow 0.3s ease;

  @media (min-width: 640px) {
    padding: 16px 24px;
  }
`;

export const HeaderBar = styled.nav`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

export const HeaderNavLinks = styled.div`
  display: none;
  align-items: center;
  gap: 28px;

  @media (min-width: 900px) {
    display: flex;
  }
`;

export const HeaderNavLink = styled.a`
  color: rgba(255, 255, 255, 0.72);
  font-size: 0.9rem;
  font-weight: 500;
  text-decoration: none;
  transition: color 0.2s ease;

  &:hover {
    color: #f29205;
  }
`;

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const HeaderMenuButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.05);
  color: #ffffff;
  cursor: pointer;
  transition: all 0.2s ease;

  @media (min-width: 900px) {
    display: none;
  }

  &:hover {
    border-color: rgba(242, 146, 5, 0.4);
    background: rgba(242, 146, 5, 0.1);
  }
`;

export const MobileMenu = styled.div`
  display: ${({ $open }) => ($open ? "flex" : "none")};
  flex-direction: column;
  gap: 4px;
  max-width: 1200px;
  margin: 12px auto 0;
  padding: 8px 0 0;
  border-top: 1px solid rgba(255, 255, 255, 0.08);

  @media (min-width: 900px) {
    display: none;
  }
`;

export const MobileMenuLink = styled.a`
  padding: 12px 16px;
  border-radius: 12px;
  color: rgba(255, 255, 255, 0.85);
  text-decoration: none;
  font-weight: 500;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.06);
    color: #f29205;
  }
`;

export const MarketingSection = styled.section`
  border-top: ${({ $first, $noBorder }) =>
    $first || $noBorder ? "none" : "1px solid var(--mkt-section-border)"};
  position: relative;
  overflow: hidden;

  ${({ $tone = "dark" }) =>
    $tone === "light" &&
    `
    --mkt-title: #141829;
    --mkt-subtitle: #5c6478;
    --mkt-card-bg: #ffffff;
    --mkt-card-border: #e2e8f4;
    --mkt-card-shadow: 0 10px 36px rgba(20, 24, 41, 0.07);
    --mkt-heading: #141829;
    --mkt-body: #5c6478;
    --mkt-quote: #3d4558;
    --mkt-section-border: rgba(20, 24, 41, 0.07);
    --mkt-icon-bg: rgba(242, 146, 5, 0.12);
    --mkt-icon-color: #f29205;
    background: linear-gradient(180deg, #f7f9fd 0%, #eef2fa 100%);

    &::before {
      content: "";
      position: absolute;
      top: -120px;
      right: -80px;
      width: 320px;
      height: 320px;
      border-radius: 50%;
      background: radial-gradient(circle, rgba(242, 146, 5, 0.08), transparent 70%);
      pointer-events: none;
    }
  `}

  ${({ $tone = "dark" }) =>
    $tone === "dark" &&
    `
    --mkt-title: #ffffff;
    --mkt-subtitle: rgba(255, 255, 255, 0.58);
    --mkt-card-bg: rgba(255, 255, 255, 0.06);
    --mkt-card-border: rgba(255, 255, 255, 0.1);
    --mkt-card-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
    --mkt-heading: #ffffff;
    --mkt-body: rgba(255, 255, 255, 0.58);
    --mkt-quote: rgba(255, 255, 255, 0.82);
    --mkt-section-border: rgba(255, 255, 255, 0.07);
    --mkt-icon-bg: rgba(255, 255, 255, 0.06);
    --mkt-icon-color: #ffffff;
    background: linear-gradient(180deg, #0c1028 0%, #101528 100%);

    &::before {
      content: "";
      position: absolute;
      inset: 0;
      pointer-events: none;
      background:
        radial-gradient(ellipse 70% 45% at 15% 0%, rgba(242, 146, 5, 0.12), transparent 55%),
        radial-gradient(ellipse 50% 35% at 90% 20%, rgba(0, 212, 255, 0.06), transparent 50%);
    }
  `}

  ${({ $tone = "dark" }) =>
    $tone === "muted" &&
    `
    --mkt-title: #141829;
    --mkt-subtitle: #5c6478;
    --mkt-card-bg: #ffffff;
    --mkt-card-border: #e8edf5;
    --mkt-card-shadow: 0 8px 28px rgba(20, 24, 41, 0.06);
    --mkt-heading: #141829;
    --mkt-body: #5c6478;
    --mkt-quote: #3d4558;
    --mkt-section-border: rgba(20, 24, 41, 0.06);
    --mkt-icon-bg: rgba(242, 146, 5, 0.1);
    --mkt-icon-color: #f29205;
    background: #ffffff;

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 50%;
      transform: translateX(-50%);
      width: min(640px, 80%);
      height: 1px;
      background: linear-gradient(
        90deg,
        transparent,
        rgba(242, 146, 5, 0.35) 50%,
        transparent
      );
      pointer-events: none;
    }
  `}
`;

export const SectionInner = styled.div`
  max-width: ${({ $narrow }) => ($narrow ? "1024px" : "1280px")};
  margin: 0 auto;
  padding: 64px 16px;
  position: relative;
  z-index: 1;

  @media (min-width: 640px) {
    padding: 96px 24px;
  }
`;

export const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 40px;

  @media (min-width: 640px) {
    margin-bottom: 64px;
  }
`;

export const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--mkt-title);
  margin: 0;

  @media (min-width: 640px) {
    font-size: 2.25rem;
  }
`;

export const SectionSubtitle = styled.p`
  color: var(--mkt-subtitle);
  font-size: 0.875rem;
  margin: 12px 0 0;
  line-height: 1.6;

  @media (min-width: 640px) {
    font-size: 1rem;
    margin-top: 16px;
  }
`;

export const HeroWrap = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 32px 16px 72px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 48px;
  align-items: center;

  @media (min-width: 640px) {
    padding: 48px 24px 96px;
  }

  @media (min-width: 1024px) {
    grid-template-columns: 1.1fr 0.9fr;
    gap: 64px;
    padding-top: 64px;
  }
`;

export const HeroContent = styled.div`
  text-align: center;

  @media (min-width: 1024px) {
    text-align: left;
  }
`;

export const HeroVisual = styled.div`
  position: relative;
  display: none;
  min-height: 380px;

  @media (min-width: 1024px) {
    display: block;
  }
`;

export const HeroVisualCard = styled.div`
  position: absolute;
  top: ${({ $top }) => $top ?? "20%"};
  left: ${({ $left }) => $left ?? "10%"};
  padding: 16px 20px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(12px);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: float 6s ease-in-out infinite;
  animation-delay: ${({ $delay }) => $delay ?? "0s"};

  @keyframes float {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-10px); }
  }

  .label {
    font-size: 0.7rem;
    color: rgba(255, 255, 255, 0.5);
    text-transform: uppercase;
    letter-spacing: 0.08em;
    margin-bottom: 4px;
  }

  .value {
    font-size: 1.5rem;
    font-weight: 800;
    color: #f29205;
  }

  .sub {
    font-size: 0.8rem;
    color: rgba(255, 255, 255, 0.6);
    margin-top: 2px;
  }
`;

export const HeroOrb = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 280px;
  height: 280px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(242, 146, 5, 0.2) 0%, transparent 70%);
  filter: blur(40px);
`;

export const HeroBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 999px;
  background: rgba(242, 146, 5, 0.12);
  border: 1px solid rgba(242, 146, 5, 0.35);
  color: #f29205;
  font-size: 0.8125rem;
  font-weight: 600;
`;

export const HeroTitle = styled.h1`
  font-size: 2.25rem;
  font-weight: 800;
  line-height: 1.15;
  color: #ffffff;
  margin: 24px 0 0;

  @media (min-width: 640px) {
    font-size: 3rem;
    margin-top: 32px;
  }

  @media (min-width: 768px) {
    font-size: 3.5rem;
  }
`;

export const HeroGradient = styled.span`
  background: linear-gradient(135deg, #fbbf24 0%, #f29205 50%, #ff6b35 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

export const HeroSubtitle = styled.p`
  color: rgba(255, 255, 255, 0.55);
  font-size: 1rem;
  line-height: 1.7;
  max-width: 540px;
  margin: 24px auto 0;

  @media (min-width: 640px) {
    font-size: 1.125rem;
    margin-top: 28px;
  }

  @media (min-width: 1024px) {
    margin-left: 0;
    margin-right: 0;
  }
`;

export const HeroActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  margin-top: 32px;

  @media (min-width: 640px) {
    flex-direction: row;
    justify-content: center;
    gap: 16px;
    margin-top: 40px;
  }

  @media (min-width: 1024px) {
    justify-content: flex-start;
  }
`;

export const HeroStats = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  justify-content: center;
  margin-top: 40px;

  @media (min-width: 1024px) {
    justify-content: flex-start;
  }
`;

export const HeroStat = styled.div`
  text-align: center;

  @media (min-width: 1024px) {
    text-align: left;
  }

  strong {
    display: block;
    font-size: 1.5rem;
    font-weight: 800;
    color: #ffffff;
    line-height: 1.2;
  }

  span {
    font-size: 0.8rem;
    color: rgba(255, 255, 255, 0.45);
  }
`;

export const HeroNote = styled.p`
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.875rem;
  margin-top: 24px;
`;

export const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;

  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 24px;
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

export const FeatureCard = styled.div`
  background: var(--mkt-card-bg);
  border: 1px solid var(--mkt-card-border);
  border-radius: 16px;
  padding: 24px;
  box-shadow: var(--mkt-card-shadow);
  transition: border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    border-color: rgba(242, 146, 5, 0.4);
    transform: translateY(-2px);
    box-shadow: 0 16px 40px rgba(242, 146, 5, 0.12);
  }
`;

export const FeatureIconBox = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: rgba(242, 146, 5, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  color: #f29205;
  font-size: 1.25rem;
`;

export const FeatureTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--mkt-heading);
  margin: 0 0 8px;
`;

export const FeatureDesc = styled.p`
  color: var(--mkt-body);
  font-size: 0.875rem;
  line-height: 1.5;
  margin: 0;
`;

export const PlansGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  align-items: stretch;

  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

export const PlanCardRoot = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 16px;
  border-radius: 16px;
  background: var(--mkt-card-bg);
  border: 1px solid var(--mkt-card-border);
  box-shadow: var(--mkt-card-shadow);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  @media (min-width: 640px) {
    padding: 24px;
  }

  &:hover {
    border-color: rgba(242, 146, 5, 0.5);
    box-shadow: 0 12px 36px rgba(242, 146, 5, 0.14);
  }
`;

export const PlanBadgesRow = styled.div`
  position: absolute;
  top: -12px;
  left: 0;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  gap: 6px;

  @media (min-width: 640px) {
    top: -16px;
    gap: 8px;
  }
`;

export const PlanBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.625rem;
  font-weight: 700;
  color: #ffffff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);

  @media (min-width: 640px) {
    padding: 4px 14px;
    font-size: 0.75rem;
  }

  ${({ $variant }) =>
    $variant === "discount"
      ? `background: #10b981;`
      : `background: #f97316;`}
`;

export const PlanCardBody = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding-top: 8px;

  @media (min-width: 640px) {
    padding-top: 16px;
  }
`;

export const PlanIconBox = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: var(--mkt-icon-bg);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
  color: var(--mkt-icon-color);
  font-size: 1.125rem;

  @media (min-width: 640px) {
    width: 48px;
    height: 48px;
    margin-bottom: 12px;
    font-size: 1.25rem;
  }
`;

export const PlanName = styled.h3`
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--mkt-heading);
  margin: 0 0 4px;

  @media (min-width: 640px) {
    font-size: 1.25rem;
    margin-bottom: 8px;
  }
`;

export const PlanDescription = styled.p`
  color: var(--mkt-body);
  font-size: 0.75rem;
  line-height: 1.5;
  margin: 0 0 16px;

  @media (min-width: 640px) {
    font-size: 0.875rem;
    margin-bottom: 24px;
  }
`;

export const PlanPriceBlock = styled.div`
  margin-top: auto;
  min-height: 64px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;

  @media (min-width: 640px) {
    min-height: 80px;
  }
`;

export const PlanPrice = styled.p`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--mkt-heading);
  margin: 0;

  @media (min-width: 640px) {
    font-size: 1.875rem;
  }
`;

export const PlanPriceSub = styled.p`
  color: var(--mkt-body);
  font-size: 0.75rem;
  margin: 4px 0 0;

  @media (min-width: 640px) {
    font-size: 0.875rem;
  }
`;

export const MarketingClosing = styled.div`
  position: relative;
  overflow: hidden;
  background: linear-gradient(180deg, #0c1028 0%, #080b1c 100%);
  color: #ffffff;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
    background:
      radial-gradient(ellipse 80% 50% at 50% 0%, rgba(242, 146, 5, 0.14), transparent 55%),
      radial-gradient(ellipse 50% 40% at 10% 90%, rgba(0, 212, 255, 0.06), transparent 50%);
  }

  > * {
    position: relative;
    z-index: 1;
  }
`;

export const MarketingFooterWrap = styled.footer`
  position: relative;
  padding: 0 16px 32px;
  overflow: hidden;


  @media (min-width: 640px) {
    padding: 0 24px 48px;
  }
`;

export const FooterGlow = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(242, 146, 5, 0.6) 20%,
    rgba(0, 212, 255, 0.4) 50%,
    rgba(255, 107, 53, 0.6) 80%,
    transparent
  );
  margin-bottom: 48px;
`;

export const FooterCard = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: linear-gradient(
    160deg,
    rgba(255, 255, 255, 0.06) 0%,
    rgba(255, 255, 255, 0.02) 40%,
    rgba(10, 14, 39, 0.8) 100%
  );
  padding: 40px 24px;
  position: relative;
  overflow: hidden;

  @media (min-width: 640px) {
    padding: 56px 48px;
  }

  &::before {
    content: "";
    position: absolute;
    top: -80px;
    right: -80px;
    width: 200px;
    height: 200px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(242, 146, 5, 0.15), transparent 70%);
    pointer-events: none;
  }

  &::after {
    content: "";
    position: absolute;
    bottom: -60px;
    left: -60px;
    width: 160px;
    height: 160px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(0, 212, 255, 0.1), transparent 70%);
    pointer-events: none;
  }
`;

export const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 40px;
  position: relative;
  z-index: 1;

  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1024px) {
    grid-template-columns: 1.4fr 1fr 1fr 1fr;
    gap: 32px;
  }
`;

export const FooterBrand = styled.div`
  p {
    color: rgba(255, 255, 255, 0.5);
    font-size: 0.9rem;
    line-height: 1.65;
    margin: 16px 0 0;
    max-width: 280px;
  }
`;

export const FooterColumn = styled.div`
  h4 {
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: #f29205;
    margin: 0 0 16px;
  }
`;

export const FooterLinkList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;

  a {
    color: rgba(255, 255, 255, 0.65);
    text-decoration: none;
    font-size: 0.9rem;
    transition: color 0.2s ease;

    &:hover {
      color: #ffffff;
    }
  }
`;

export const FooterBottom = styled.div`
  max-width: 1200px;
  margin: 24px auto 0;
  padding-top: 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;

  @media (min-width: 640px) {
    flex-direction: row;
    justify-content: space-between;
    text-align: left;
  }
`;

export const FooterCopy = styled.p`
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.8125rem;
  margin: 0;
`;

export const FooterBottomLinks = styled.div`
  display: flex;
  gap: 20px;

  a {
    color: rgba(255, 255, 255, 0.45);
    font-size: 0.8125rem;
    text-decoration: none;
    transition: color 0.2s ease;

    &:hover {
      color: #f29205;
    }
  }
`;

export const StepsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 32px;
  }
`;

export const StepCard = styled.div`
  position: relative;
  padding: 32px 24px;
  border-radius: 20px;
  background: var(--mkt-card-bg);
  border: 1px solid var(--mkt-card-border);
  box-shadow: var(--mkt-card-shadow);
  transition: all 0.25s ease;

  &:hover {
    border-color: rgba(242, 146, 5, 0.35);
    transform: translateY(-4px);
    box-shadow: 0 20px 50px rgba(242, 146, 5, 0.12);
  }
`;

export const StepNumber = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 14px;
  background: linear-gradient(135deg, #f29205 0%, #ff6b35 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.125rem;
  font-weight: 800;
  color: #ffffff;
  margin-bottom: 20px;
  box-shadow: 0 8px 24px rgba(242, 146, 5, 0.3);
`;

export const StepTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--mkt-heading);
  margin: 0 0 10px;
`;

export const StepDesc = styled.p`
  color: var(--mkt-body);
  font-size: 0.875rem;
  line-height: 1.6;
  margin: 0;
`;

export const TestimonialsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;

  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

export const TestimonialCard = styled.blockquote`
  margin: 0;
  padding: 28px 24px;
  border-radius: 20px;
  background: var(--mkt-card-bg);
  border: 1px solid var(--mkt-card-border);
  box-shadow: var(--mkt-card-shadow);
  display: flex;
  flex-direction: column;
  gap: 20px;
  transition: border-color 0.25s ease, transform 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    border-color: rgba(242, 146, 5, 0.35);
    transform: translateY(-3px);
    box-shadow: 0 16px 40px rgba(242, 146, 5, 0.1);
  }
`;

export const TestimonialStars = styled.div`
  display: flex;
  gap: 4px;
  color: #f29205;
  font-size: 0.875rem;
  letter-spacing: 2px;
`;

export const TestimonialQuote = styled.p`
  color: var(--mkt-quote);
  font-size: 0.9375rem;
  line-height: 1.65;
  margin: 0;
  flex: 1;
  font-style: italic;
`;

export const TestimonialAuthor = styled.footer`
  display: flex;
  align-items: center;
  gap: 14px;
  margin: 0;
`;

export const TestimonialAvatar = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: linear-gradient(135deg, #f29205 0%, #ff6b35 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  font-weight: 800;
  color: #ffffff;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(242, 146, 5, 0.3);
`;

export const TestimonialInfo = styled.div`
  strong {
    display: block;
    color: var(--mkt-heading);
    font-size: 0.9rem;
    font-weight: 700;
    margin-bottom: 2px;
  }

  span {
    color: var(--mkt-body);
    font-size: 0.8rem;
  }
`;

export const CtaBanner = styled.section`
  max-width: 1200px;
  margin: 0 auto;
  padding: 80px 16px 0;

  @media (min-width: 640px) {
    padding: 96px 24px 0;
  }
`;

export const CtaBannerInner = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 24px;
  padding: 48px 32px;
  text-align: center;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(242, 146, 5, 0.28);
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.25);

  @media (min-width: 640px) {
    padding: 64px 48px;
  }

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(ellipse at 30% 0%, rgba(242, 146, 5, 0.12), transparent 60%);
    pointer-events: none;
  }

  h2 {
    position: relative;
    font-size: 1.75rem;
    font-weight: 800;
    color: #ffffff;
    margin: 0 0 12px;

    @media (min-width: 640px) {
      font-size: 2.25rem;
    }
  }

  p {
    position: relative;
    color: rgba(255, 255, 255, 0.6);
    font-size: 1rem;
    max-width: 480px;
    margin: 0 auto 28px;
    line-height: 1.6;
  }
`;

export const CtaBannerActions = styled.div`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
`;

export const NavDesktopOnly = styled.span`
  display: none;

  @media (min-width: 640px) {
    display: inline-flex;
  }
`;

export const NavMobileOnly = styled.span`
  display: inline-flex;

  @media (min-width: 640px) {
    display: none;
  }
`;

export const MarketingButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-weight: 600;
  border-radius: 999px;
  transition: all 0.2s ease;
  cursor: pointer;
  text-decoration: none;
  border: none;
  font-family: inherit;
  white-space: nowrap;

  ${({ $size }) => {
    switch ($size) {
      case "sm":
        return `padding: 8px 12px; font-size: 0.875rem;`;
      case "lg":
        return `padding: 14px 24px; font-size: 1rem;`;
      default:
        return `padding: 10px 16px; font-size: 0.875rem;`;
    }
  }}

  ${({ $variant }) => {
    switch ($variant) {
      case "ghost":
        return `
          background: transparent;
          color: rgba(255, 255, 255, 0.65);
          &:hover { color: #ffffff; background: rgba(255, 255, 255, 0.06); }
        `;
      case "secondary":
        return `
          background: rgba(255, 255, 255, 0.06);
          border: 2px solid rgba(255, 255, 255, 0.14);
          color: rgba(255, 255, 255, 0.88);
          width: 100%;
          &:hover {
            background: linear-gradient(135deg, #f29205 0%, #ff6b35 100%);
            border-color: rgba(242, 146, 5, 0.5);
            color: #ffffff;
            box-shadow: 0 8px 24px rgba(242, 146, 5, 0.25);
          }
        `;
      default:
        return `
          background: linear-gradient(135deg, #f29205 0%, #ff6b35 100%);
          color: #ffffff;
          box-shadow: 0 4px 15px rgba(242, 146, 5, 0.3);
          &:hover {
            transform: translateY(-2px);
            box-shadow: 0 6px 20px rgba(242, 146, 5, 0.4);
          }
        `;
    }
  }}
`;

export const MarketingLinkButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-weight: 600;
  border-radius: 999px;
  transition: all 0.2s ease;
  cursor: pointer;
  text-decoration: none;
  border: none;
  font-family: inherit;
  white-space: nowrap;

  ${({ $size }) => {
    switch ($size) {
      case "sm":
        return `
          padding: 8px 12px;
          font-size: 0.875rem;
          svg { font-size: 1.05rem; }
        `;
      case "lg":
        return `
          padding: 14px 24px;
          font-size: 1rem;
          svg { font-size: 1.35rem; }
        `;
      default:
        return `
          padding: 10px 16px;
          font-size: 0.875rem;
          svg { font-size: 1.15rem; }
        `;
    }
  }}

  svg {
    flex-shrink: 0;
  }

  ${({ $variant }) => {
    switch ($variant) {
      case "ghost":
        return `
          background: transparent;
          color: rgba(255, 255, 255, 0.65);
          &:hover { color: #ffffff; background: rgba(255, 255, 255, 0.06); }
        `;
      case "secondary":
        return `
          background: rgba(255, 255, 255, 0.06);
          border: 2px solid rgba(255, 255, 255, 0.14);
          color: rgba(255, 255, 255, 0.88);
          &:hover {
            background: linear-gradient(135deg, #f29205 0%, #ff6b35 100%);
            border-color: rgba(242, 146, 5, 0.5);
            color: #ffffff;
            box-shadow: 0 8px 24px rgba(242, 146, 5, 0.25);
            transform: translateY(-1px);
          }
        `;
      case "outline":
        return `
          background: #ffffff;
          border: 2px solid #c5cedf;
          color: #141829;
          &:hover {
            background: linear-gradient(135deg, #f29205 0%, #ff6b35 100%);
            border-color: rgba(242, 146, 5, 0.5);
            color: #ffffff;
            box-shadow: 0 8px 24px rgba(242, 146, 5, 0.25);
            transform: translateY(-1px);
          }
        `;
      case "whatsapp":
        return `
          background: linear-gradient(135deg, #25d366 0%, #128c7e 100%);
          color: #ffffff;
          box-shadow: 0 8px 24px rgba(37, 211, 102, 0.35);
          &:hover {
            color: #ffffff;
            transform: translateY(-2px);
            box-shadow: 0 12px 28px rgba(37, 211, 102, 0.45);
          }
        `;
      default:
        return `
          background: linear-gradient(135deg, #f29205 0%, #ff6b35 100%);
          color: #ffffff;
          box-shadow: 0 4px 15px rgba(242, 146, 5, 0.3);
          &:hover {
            color: #ffffff;
            transform: translateY(-2px);
            box-shadow: 0 6px 20px rgba(242, 146, 5, 0.4);
          }
        `;
    }
  }}
`;

export const FaqList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const FaqItem = styled.div`
  border-radius: 16px;
  border: 1px solid var(--mkt-card-border);
  background: var(--mkt-card-bg);
  box-shadow: var(--mkt-card-shadow);
  overflow: hidden;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  ${({ $open }) =>
    $open &&
    `
    border-color: rgba(242, 146, 5, 0.35);
    box-shadow: 0 12px 32px rgba(242, 146, 5, 0.1);
  `}
`;

export const FaqQuestion = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 24px;
  background: transparent;
  border: none;
  text-align: left;
  font-family: inherit;
  font-size: 1rem;
  font-weight: 600;
  color: var(--mkt-heading);
  cursor: pointer;

  svg {
    flex-shrink: 0;
    color: #f29205;
    transition: transform 0.2s ease;
  }

  &[aria-expanded="true"] svg {
    transform: rotate(180deg);
  }
`;

export const FaqAnswer = styled.div`
  max-height: ${({ $open }) => ($open ? "240px" : "0")};
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  overflow: hidden;
  padding: ${({ $open }) => ($open ? "0 24px 20px" : "0 24px")};
  color: var(--mkt-body);
  font-size: 0.9375rem;
  line-height: 1.65;
  transition: max-height 0.25s ease, opacity 0.2s ease, padding 0.2s ease;
`;

export const ContactCards = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;

  @media (min-width: 768px) {
    grid-template-columns: 1.2fr 0.8fr;
    align-items: stretch;
  }
`;

export const ContactCard = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 24px;
  padding: 28px 24px;
  border-radius: 20px;
  border: 1px solid var(--mkt-card-border);
  background: var(--mkt-card-bg);
  box-shadow: var(--mkt-card-shadow);

  ${({ $featured }) =>
    $featured &&
    `
    border-color: rgba(37, 211, 102, 0.28);
    background: linear-gradient(180deg, #ffffff 0%, #f3fcf6 100%);
  `}
`;

export const ContactHighlight = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
`;

export const ContactIcon = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.35rem;
  flex-shrink: 0;
  background: rgba(242, 146, 5, 0.12);
  color: #f29205;

  ${({ $variant }) =>
    $variant === "whatsapp" &&
    `
    background: rgba(37, 211, 102, 0.14);
    color: #128c7e;
  `}
`;

export const ContactInfo = styled.div`
  p {
    margin: 8px 0 0;
    color: var(--mkt-body);
    font-size: 0.875rem;
    line-height: 1.5;
  }
`;

export const ContactLabel = styled.span`
  display: block;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--mkt-body);
  margin-bottom: 4px;
`;

export const ContactValue = styled.strong`
  display: block;
  font-size: 1.25rem;
  color: var(--mkt-heading);
`;

export const WhatsAppFabWrap = styled.div`
  position: fixed;
  right: 20px;
  bottom: 20px;
  z-index: 120;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transform: translateY(${({ $visible }) => ($visible ? "0" : "12px")});
  pointer-events: ${({ $visible }) => ($visible ? "auto" : "none")};
  transition: opacity 0.25s ease, transform 0.25s ease;

  @media (min-width: 640px) {
    right: 28px;
    bottom: 28px;
  }
`;

export const WhatsAppFabBubble = styled.div`
  position: relative;
  max-width: 240px;
  padding: 14px 40px 14px 16px;
  border-radius: 16px 16px 4px 16px;
  background: #ffffff;
  border: 1px solid #e2e8f4;
  box-shadow: 0 16px 40px rgba(20, 24, 41, 0.14);
  animation: fabPop 0.35s ease;

  @keyframes fabPop {
    from {
      opacity: 0;
      transform: translateY(8px) scale(0.96);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  strong {
    display: block;
    color: #141829;
    font-size: 0.9rem;
    margin-bottom: 4px;
  }

  span {
    color: #5c6478;
    font-size: 0.8125rem;
    line-height: 1.45;
  }

  button {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 24px;
    height: 24px;
    border: none;
    border-radius: 999px;
    background: #f4f6fb;
    color: #5c6478;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 0.7rem;
  }
`;

export const WhatsAppFabButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 58px;
  height: 58px;
  border-radius: 999px;
  background: linear-gradient(135deg, #25d366 0%, #128c7e 100%);
  color: #ffffff;
  text-decoration: none;
  box-shadow: 0 12px 32px rgba(37, 211, 102, 0.42);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  animation: fabPulse 2.4s ease-in-out infinite;

  @keyframes fabPulse {
    0%, 100% { box-shadow: 0 12px 32px rgba(37, 211, 102, 0.42); }
    50% { box-shadow: 0 12px 36px rgba(37, 211, 102, 0.58), 0 0 0 8px rgba(37, 211, 102, 0.12); }
  }

  svg {
    font-size: 1.6rem;
  }

  &:hover {
    transform: translateY(-2px) scale(1.02);
    box-shadow: 0 16px 36px rgba(37, 211, 102, 0.5);
  }
`;

export const MobileAppBannerBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: linear-gradient(135deg, #0f3460 0%, #16213e 100%);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);

  @media (min-width: 641px) {
    display: none;
  }
`;

export const MobileAppBannerIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  border-radius: 10px;
  background: rgba(242, 146, 5, 0.15);
  color: #f29205;
  font-size: 18px;
`;

export const MobileAppBannerText = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 0.8rem;
  font-weight: 700;
  color: #ffffff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const MobileAppBannerCta = styled.a`
  flex-shrink: 0;
  padding: 7px 14px;
  border-radius: 999px;
  background: linear-gradient(135deg, #f29205 0%, #ff6b35 100%);
  color: #ffffff;
  font-size: 0.78rem;
  font-weight: 700;
  text-decoration: none;
  white-space: nowrap;
`;

export const MobileAppBannerClose = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  font-size: 13px;

  &:hover {
    color: rgba(255, 255, 255, 0.85);
    background: rgba(255, 255, 255, 0.08);
  }
`;

/* ---- Específicos do GBinary ---- */

export const LogoLink = styled.a`
  display: inline-flex;
  align-items: center;

  img {
    display: block;
    height: ${({ $size }) => $size || 40}px;
    width: auto;
  }
`;

/* Print do dashboard no hero, com os cards flutuantes por cima */
export const HeroScreen = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.45);

  img {
    display: block;
    width: 100%;
    height: auto;
  }
`;

export const VideoFrame = styled.div`
  position: relative;
  max-width: 880px;
  margin: 0 auto;
  aspect-ratio: 16 / 9;
  border-radius: 24px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.4);
  background: #000;

  iframe {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border: 0;
  }
`;

export const AboutCard = styled.div`
  max-width: 820px;
  margin: 0 auto;
  padding: 32px 24px;
  border-radius: 24px;
  background: var(--mkt-card-bg);
  border: 1px solid var(--mkt-card-border);
  box-shadow: var(--mkt-card-shadow);

  @media (min-width: 640px) {
    padding: 48px;
  }

  p {
    color: var(--mkt-quote);
    font-size: 0.95rem;
    line-height: 1.75;
    margin: 0 0 16px;

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

export const ThreeColumnPlansGrid = styled(PlansGrid)`
  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

export const TwoColumnTestimonialsGrid = styled(TestimonialsGrid)`
  @media (min-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;
